import '.eu{height:100svh;}.ssr-plugin'
import '.ev{width:100svw;}.ssr-plugin'
import { ReactNode, useEffect } from 'react'
import { MagicLinkLogin } from './MagicLinkLogin.js'
import { EmailPasswordLogin } from './EmailPasswordLogin.js'
import { useClient } from '@based/sdk/react'
import { styled } from 'inlines'
import { useCmsParams } from '../../hooks/useCmsParams.js'
import {
  arrayBufferToBase64url,
  base64urlToArrayBuffer,
} from '../Passkeys/utils.js'
import { useToast } from '../Toast/index.js'

export type AuthPageProps = {
  appName: ReactNode
  mode: 'magic-link' | 'email-password'
  onSubmit?: (args: {
    email: string
    code?: string
    password?: string
  }) => Promise<void>
  onResend?: (args: {
    email: string
    code?: string
    password?: string
  }) => Promise<void>
  onCreateAccount?: () => Promise<void>
  validateInviteEndpoint?: string
  loginEndpoint?: string
  usePasskeys?: boolean
  // TODO: onPasswordReset, validate password, validate email, etc
}

export const AuthPage = (props: AuthPageProps) => {
  const {
    mode,
    validateInviteEndpoint = 'ui-auth-validate-invite',
    loginEndpoint = 'ui-auth-login',
    usePasskeys,
  } = props
  const client = useClient()
  const [params, go] = useCmsParams()
  const toast = useToast()

  useEffect(() => {
    if (usePasskeys) {
      if (!window.PublicKeyCredential) {
        console.debug('Passkeys not supported in this browser')
        return
      }

      const abortController = new AbortController()

      PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
        .then(async (available) => {
          if (!available) {
            console.debug('No platform authenticator available')
            return
          }

          const timeoutId = setTimeout(() => {
            console.debug('Passkey authentication timeout')
            abortController.abort()
          }, 300000) // 5 minutes

          try {
            const options = await client.call('passkeys-auth')
            options.challenge = base64urlToArrayBuffer(options.challenge)

            let assertion: PublicKeyCredential
            try {
              assertion = (await navigator.credentials.get({
                publicKey: options,
                signal: abortController.signal,
              })) as PublicKeyCredential
            } catch (error) {
              if (error instanceof Error) {
                // User cancelled the authentication - this is normal behavior
                if (
                  error.name === 'NotAllowedError' ||
                  error.name === 'AbortError'
                ) {
                  console.debug('Passkey authentication cancelled by user')
                  return
                }
                if (!error.message?.includes('port')) {
                  console.error('Passkey authentication error:', error)
                }
              }
              return // Exit if credential request failed
            } finally {
              clearTimeout(timeoutId)
            }

            if (assertion?.response) {
              const response =
                assertion?.response as AuthenticatorAssertionResponse

              const res = await client.call('passkeys-auth-verify', {
                id: assertion.id,
                type: assertion.type,
                authenticatorData: arrayBufferToBase64url(
                  response.authenticatorData,
                ),
                clientDataJSON: arrayBufferToBase64url(response.clientDataJSON),
                signature: arrayBufferToBase64url(response.signature),
                userHandle: response.userHandle
                  ? arrayBufferToBase64url(response.userHandle)
                  : null,
              })

              if (res.error) {
                toast('Error using Passkey', {
                  description: res.error,
                  icon: 'error-filled',
                  color: 'red',
                })
              } else {
                go({ section: '' }, true)
              }
            }
          } catch (error) {
            console.debug('Error during passkey authentication:', error)
          }
        })
        .catch((error) => {
          console.debug('Error checking passkey availability:', error)
        })

      return () => {
        abortController.abort()
      }
    }
  }, [])

  useEffect(() => {
    if (params.t) {
      client.call(validateInviteEndpoint, { token: params.t })
    }
  }, [params])

  return (
    <div className="eu ev"
    >
      {mode === 'magic-link' ? (
        <MagicLinkLogin
          onSubmit={async ({ email, code }) => {
            await client
              .call(loginEndpoint, {
                email,
                code,
              })
              .then(() => go({ section: '' }, true))
          }}
          {...props}
        />
      ) : (
        <EmailPasswordLogin
          onSubmit={async ({ email, password }) => {
            await client
              .call(loginEndpoint, {
                mode,
                email,
                password,
              })
              .then(() => go({ section: '' }, true))
          }}
          {...props}
        />
      )}
    </div>
  )
}
